// G4 — the gloss / translation surface for a normalized original-pane selection
// (non-English books only; SPEC "Translation / gloss"). A quiet lexicon card
// anchored to the selection: the source-language quote, then Gloss / Translation
// / Literal tabs (lekterion's popover with Read-aloud dropped, Literal promoted).
//
// Reactive flow (react-useeffect-discipline): the client computes the SAME cache
// key the action re-derives (convex/model/selection), subscribes with
// useQuery(ai.getCached) — a hit renders for anyone, signed in or not. A
// resolved miss NEVER spends on its own (PORT-PREP "cache-first, explicit
// generate", lekterion canonical): signed-in → a "Gloss & translate" button
// whose click handler fires the gated action; signed-out → the inline "sign in
// to translate" state (the one auth touch on the reading surface). Target
// language is the global pref via the target-lang provider.
//
// The engagement gate (lekterion §13, ported): a single word whose translation
// is ALREADY cached — this exact selection, or a `derived` per-word row fanned
// out of some phrase (ai.wordTranslation) — is free, so before handing it over
// the reader must pick the word out of orthographic near-misses (confounders,
// derived at runtime from the chapter text by @bindery/shared/reader/
// confounders; the chip row is @bindery/ui's GateChallenge). An UNCACHED
// word has no gate (glossing it costs a model call — don't tax the reader),
// and a word yielding no confounder falls open ungated. The unlock is NEVER
// persisted: this component remounts per selection (keyed in SelectionLayer),
// so reselecting the same word re-gates — rematch-for-learning, by design.
// While the gate stands, no generate affordance renders: nothing is spent and
// nothing revealed until the match.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { BookOpen, Languages, SplitSquareHorizontal } from "lucide-react";
import { toast } from "sonner";

import { api } from "@bindery/convex-retells/_generated/api";
import { Button } from "@bindery/ui/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@bindery/ui/components/ui/tabs";
import { useTargetLang } from "@/components/target-lang-context";
import { useLogger } from "@/logging";
import { getSessionId } from "@/logging";
import { signIn, useIsAuthenticated } from "@/lib/auth";
import { getPref, setPref } from "@/lib/prefs";
import {
  limitReachedScope,
  readQuotaError,
  retryPhrase,
  type QuotaRefusal,
} from "@/lib/quota-error";
import { useCoarseNow } from "@/lib/use-coarse-now";
import { useLastLoaded } from "@/lib/use-last-loaded";
import { cn } from "@bindery/ui/lib/utils";
import { SubscribePrompt } from "./subscribe-prompt";
import {
  describeGlossFailure,
  glossInputFailure,
  type GlossFailure,
} from "./gloss-failure";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import type { AdminRole } from "@bindery/convex-retells/model/adminRole";
import type { GlossResult } from "@bindery/convex-retells/model/gloss";
import {
  citationFor,
  selectionHash,
  spanTokenCount,
  type NormalizedSpan,
} from "@bindery/convex-retells/model/selection";
import { HOUR_MS } from "@bindery/convex-retells/model/translationQuota";
import {
  colocationForModel,
  getOptions,
} from "@bindery/shared/reader/confounders";
import {
  challengeableConfounders,
  gateStatus,
  type GatePick,
} from "@bindery/shared/reader/gate";
import { GateChallenge } from "@bindery/ui/components/reader/gate-challenge";
import { foldQuote } from "@bindery/convex-retells/model/wordGloss";

type Facet = "gloss" | "translation" | "literal";

const FACETS: { id: Facet; label: string; Icon: typeof BookOpen }[] = [
  { id: "gloss", label: "Gloss", Icon: BookOpen },
  { id: "translation", label: "Translation", Icon: Languages },
  { id: "literal", label: "Literal", Icon: SplitSquareHorizontal },
];

// One id for every founder notice, so a founder who keeps translating past the
// cap updates the same toast in place instead of stacking a new one per lookup.
const FOUNDER_NOTICE_ID = "founder-quota-notice";

/**
 * Tell a founder they have crossed the cap every other reader is held to.
 *
 * The founder is never refused, so the `refusal` → `SubscribePrompt` path below
 * — the whole signal an ordinary reader (an `admin`-role account included) gets
 * — never fires for them. This is deliberately the weakest form of notice: the
 * translation they asked for has ALREADY been delivered by the time this runs,
 * so the copy must not imply they were blocked. Dismissing silences it for an
 * hour rather than forever; the point is to be noticed again next session, not
 * to nag inside one.
 *
 * `quota` is the meter as of the last round trip — i.e. BEFORE the call that
 * just succeeded — which is exactly the question being asked: would this call
 * have been refused had the caller been anyone else?
 */
function notifyFounderPastLimit(
  role: AdminRole | null | undefined,
  quota: Parameters<typeof limitReachedScope>[0],
  now: number,
): void {
  if (role !== "founder") return;
  const scope = limitReachedScope(quota);
  if (scope === null) return;
  if (getPref("founderNoticeSilencedUntil", 0) > now) return;
  toast("Translation limit reached", {
    id: FOUNDER_NOTICE_ID,
    description: `You are past the normal ${
      scope === "day" ? "daily" : "hourly"
    } translation limit. As founder your access carries on uncapped — every other reader would be waiting here.`,
    duration: Infinity, // a notice worth acting on shouldn't time out unseen
    action: {
      label: "Dismiss",
      onClick: () => {
        // The clock at DISMISSAL, not the `now` this toast was raised on.
        // `duration: Infinity` lets it sit on screen for as long as the reader
        // likes, so silencing from the raise-time clock buys less than the
        // promised hour — and, once the toast has been up for longer than an
        // hour, an already-elapsed deadline that silences nothing at all.
        setPref("founderNoticeSilencedUntil", Date.now() + HOUR_MS);
        toast.dismiss(FOUNDER_NOTICE_ID);
      },
    },
  });
}

/** A thrown Convex `unauthenticated` error (stale/absent session at call time). */
function isUnauthenticated(error: unknown): boolean {
  return (
    error instanceof ConvexError &&
    typeof error.data === "object" &&
    error.data !== null &&
    (error.data as { code?: unknown }).code === "unauthenticated"
  );
}

function FacetText({ value, lang }: { value: string; lang?: string }) {
  if (value === "") {
    return (
      <p className="text-muted-foreground font-serif text-sm italic">
        Not available for this selection.
      </p>
    );
  }
  return (
    <p
      className="text-foreground font-serif text-sm leading-relaxed"
      lang={lang}
    >
      {value}
    </p>
  );
}

/**
 * The explicit "spend one call" affordance on a resolved miss. One button
 * element across its idle / in-flight / failed states, so a reader who
 * pressed it keeps focus on it while the call runs (`aria-disabled`, never
 * `disabled`, which would drop focus to <body> — the same reasoning as the
 * gate's chips). The prompt line above it says what the press does, or why
 * the last one did not land.
 */
function GenerateControl({
  prompt,
  label,
  generating,
  failure,
  onGenerate,
}: {
  prompt: string;
  label: string;
  generating: boolean;
  failure: GlossFailure | null;
  onGenerate: () => void;
}) {
  // A failure retrying cannot fix (a selection the server refuses outright)
  // gets its explanation and no button: "Try again" under it would be a
  // promise the same request can never keep.
  if (failure !== null && !failure.retryable) {
    return (
      <p className="text-destructive font-serif text-sm" role="alert">
        {failure.message}
      </p>
    );
  }
  return (
    <div className="flex flex-col items-start gap-2">
      {failure !== null ? (
        <p className="text-destructive font-serif text-sm" role="alert">
          {failure.message}
        </p>
      ) : (
        <p className="text-muted-foreground font-serif text-sm">{prompt}</p>
      )}
      <Button
        type="button"
        size="sm"
        aria-disabled={generating}
        className={cn(generating && "pointer-events-none")}
        onClick={() => {
          if (!generating) onGenerate();
        }}
      >
        {generating ? (
          <span className="animate-pulse motion-reduce:animate-none">
            Glossing…
          </span>
        ) : failure !== null ? (
          "Try again"
        ) : (
          label
        )}
      </Button>
    </div>
  );
}

/**
 * What the gate says out loud. The shake and the fade are the only feedback a
 * pick has ever had, which leaves a screen-reader user hearing a button click
 * and then nothing at all — WCAG 4.1.3 (Status Messages).
 *
 * `slot` alternates 0/1 between announcements. A live region rewritten with
 * the SAME string is commonly not re-announced, and "not quite" is exactly the
 * message a reader hears twice in a row; clearing one region and filling the
 * other is a genuine insertion every time, without a clear-then-set timer.
 */
interface GateAnnouncement {
  text: string;
  slot: 0 | 1;
}

/** The next announcement, alternating off the previous one (`null` on mount). */
function announce(
  previous: GateAnnouncement | null,
  text: string,
): GateAnnouncement {
  return { text, slot: previous?.slot === 0 ? 1 : 0 };
}

/** Candidates for `firstTabbable` — narrowed by tab index, not by this list. */
const FOCUSABLE = "a[href], button, input, select, textarea, [tabindex]";

/** First element inside `root` the Tab order would reach, or `null`. */
function firstTabbable(root: HTMLElement): HTMLElement | null {
  for (const el of root.querySelectorAll<HTMLElement>(FOCUSABLE)) {
    if (el.tabIndex >= 0 && !el.hasAttribute("disabled")) return el;
  }
  return null;
}

/**
 * The identification challenge standing between the reader and a cached
 * translation — retells' shell around the shared `GateChallenge`. Set as a
 * printed exercise in the card's own grammar: an Alegreya SC small-caps rubric
 * over hairline-ruled serif chips. The shared row runs the round and its
 * cooldown (shake, harder after repeated misses; fade; a fresh shuffled
 * set), styled here off its data attributes; the parent owns the unlock and
 * the logging via `onPick`.
 */
function GateShell({
  quote,
  forms,
  lang,
  onPick,
}: {
  quote: string;
  forms: readonly string[];
  lang: string;
  onPick: (pick: GatePick) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5 pt-1 pb-0.5">
      {/* The rubric row, in the reader's established contents-page grammar:
          Alegreya SC small-caps + a dotted hairline leader. */}
      <div className="flex items-center gap-2">
        <span className="font-smcp text-muted-foreground shrink-0 text-[13px] tracking-[0.219em] lowercase">
          Match the word
        </span>
        <span
          aria-hidden="true"
          className="border-rule h-0 flex-1 border-b border-dotted"
        />
      </div>
      <GateChallenge
        word={quote}
        forms={forms}
        // The consensus key's fold (`aiCache.quoteLower`), so the logged
        // `word` / `chosen` compare against the same keys the cache uses.
        normalize={foldQuote}
        lang={lang}
        label="Pick the word you selected to reveal its translation"
        onPick={onPick}
        className={cn(
          "flex flex-wrap gap-2 transition-opacity duration-150 motion-reduce:transition-none",
          "motion-safe:data-[shake=light]:animate-gate-shake-light",
          "motion-safe:data-[shake=hard]:animate-gate-shake-hard",
          "data-[phase=faded]:opacity-0 data-[phase=faded]:duration-[260ms]",
        )}
        chipClassName={cn(
          // `--rule`, not an alpha: this border is the ONLY thing drawing
          // the chip, so WCAG 1.4.11 wants 3:1 of it (index.css).
          "rounded-sm border border-rule px-2.5 py-1",
          "font-serif text-sm leading-snug text-foreground",
          "hover:bg-accent hover:text-accent-foreground",
          "focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:outline-none",
          // The cooldown keeps chips focusable (`aria-disabled`); this stops
          // the hover styling from inviting a press it will ignore.
          "data-[locked]:pointer-events-none",
        )}
      />
      <p className="text-muted-foreground font-serif text-xs italic">
        Its translation is waiting behind the right spelling.
      </p>
    </div>
  );
}

export function GlossPopover({
  model,
  norm,
  paragraph,
  bookSlug,
  chapterNumber,
  sourceLanguage,
  ownsFocus,
  action,
}: {
  /** The ORIGINAL document's model — confounder tables + token counts. */
  model: DocumentModel;
  norm: NormalizedSpan;
  /** Text of the blocks the selection touches — for the up-front input check
   *  only; the server cuts its own passage from `norm`'s offsets. */
  paragraph: string;
  bookSlug: string;
  chapterNumber: number;
  sourceLanguage: string;
  /**
   * Does the reader's focus live in this card? True only for a word-navigation
   * capture, which is the one entry path that hands the popover its focus (see
   * `CaptureVia` in `selection-layer.tsx`). When it does, the card is
   * responsible for keeping it: its three bodies swap under the reader as
   * queries resolve, and each swap unmounts the element they were focused on.
   */
  ownsFocus: boolean;
  /**
   * An extra control for this card, built from the span it is glossing. The
   * reader passes nothing; the admin workbench passes its "Set anchor" button.
   * Rendered high in the card, and NEVER while the gate stands — the gate masks
   * the headword deliberately, and an action naming the span would hand back
   * the answer it is withholding.
   *
   * Being the first tabbable element in the card is the POINT, not an accident:
   * a word-navigation capture is the one entry path Radix autofocuses into
   * (`selection-layer.tsx`'s `onOpenAutoFocus`), and its focus scope focuses the
   * first tabbable candidate in DOM order — so with an action present the
   * admin reaches it in zero Tabs, exactly as the reader reaches the Gloss tab.
   */
  action?: (norm: NormalizedSpan) => ReactNode;
}) {
  const [facet, setFacet] = useState<Facet>("gloss");
  // Not `useConvexAuth` — this app mounts a plain `ConvexProvider`, so that
  // hook throws (see `useIsAuthenticated` in @/lib/auth).
  const isAuthenticated = useIsAuthenticated();
  const { targetLang } = useTargetLang();
  const log = useLogger();
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  const citation = useMemo(
    () => citationFor(bookSlug, chapterNumber),
    [bookSlug, chapterNumber],
  );

  // Frozen cache key (async — Web Crypto), then the reactive cache check.
  const [key, setKey] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    void selectionHash(norm.quote).then((k) => {
      if (active) setKey(k);
    });
    return () => {
      active = false;
    };
  }, [norm.quote]);

  const cached = useQuery(
    api.ai.getCached,
    key ? { selectionHash: key, citation, targetLang } : "skip",
  );

  // Count the cache HIT here, because here is the only place it is observable:
  // a hit is served straight off this subscription and never reaches
  // `glossSelection`, so a server-side count would report a hit rate near
  // zero. Misses are counted by the action, which is where they happen.
  const countedHitRef = useRef<string | null>(null);
  const recordGloss = useMutation(api.glossStats.record);
  const recordGlossRef = useRef(recordGloss);
  useEffect(() => {
    recordGlossRef.current = recordGloss;
  });
  useEffect(() => {
    if (key === null || cached === undefined || cached === null) return;
    // Keyed the same way `requestKey` is: a mid-popover language change
    // is a different cache key and a different lookup.
    const hitKey = `${key}:${citation}:${targetLang}`;
    if (countedHitRef.current === hitKey) return;
    countedHitRef.current = hitKey;
    void recordGlossRef
      .current({ sessionKey: getSessionId(), bookSlug, hit: true })
      // Telemetry never surfaces to the reader (see `use-reading-telemetry`).
      .catch(() => undefined);
  }, [key, cached, citation, targetLang, bookSlug]);

  // The gate's raw material: single-word selections get ranked confounders
  // from the chapter's own colocation tables (memoized per model). Both are
  // synchronous — no loading state, the gate's shape is known at first render.
  const singleWord = useMemo(
    () => spanTokenCount(model, norm.start, norm.end) === 1,
    [model, norm.start, norm.end],
  );
  // Re-surfaced to the selection's case and quote glyphs, with any that
  // would collide with the word or each other dropped — a round never deals
  // two identical chips.
  const forms = useMemo(
    () =>
      singleWord
        ? challengeableConfounders(
            norm.quote,
            getOptions(
              foldQuote(norm.quote),
              colocationForModel(model, foldQuote),
            ),
            foldQuote,
          )
        : [],
    [singleWord, model, norm.quote],
  );

  // Cross-corpus consensus — asked only for a single word whose exact-key
  // check resolved to a miss (a hit already carries the better, contextual
  // answer). Free for anyone: it reads the cache, it can never generate.
  const consensus = useQuery(
    api.ai.wordTranslation,
    singleWord && key !== null && cached === null
      ? { word: norm.quote, sourceLang: sourceLanguage, targetLang }
      : "skip",
  );
  // Still waiting to learn whether anything is cached for this single word —
  // the render below holds at "Looking this up…" and offers no generate
  // button yet, so nothing is spent or revealed prematurely.
  const consensusPending =
    singleWord && key !== null && cached === null && consensus === undefined;

  // The gate stands when something IS cached (exact hit, or consensus from a
  // derived row) for a single word that yields at least one confounder; a
  // word with none falls open ungated (lekterion's documented behavior). The
  // unlock is component-local and never persisted — remounting re-gates.
  const [unlocked, setUnlocked] = useState(false);
  // Lives HERE, not in `GateShell`: a correct pick unmounts that subtree
  // at once, so an announcement owned by it would never be heard.
  const [announcement, setAnnouncement] = useState<GateAnnouncement | null>(
    null,
  );
  const exactHit = cached !== undefined && cached !== null;
  const consensusHit = consensus !== undefined && consensus !== null;
  // `pending` (lookups unresolved) is not gated here: the body holds at
  // "Looking this up…" and offers nothing until it resolves (`consensusPending`).
  const gated =
    gateStatus({
      singleWord,
      cached:
        exactHit || consensusHit
          ? true
          : key === null || cached === undefined || consensusPending
            ? undefined
            : false,
      forms,
      unlocked,
    }) === "gated";

  const generate = useAction(api.ai.glossSelection);
  // The request in flight and the last failure, each tagged with the request
  // key it answers. Tagging (rather than clearing on change) is what lets a
  // mid-popover language change fall back to the idle "Gloss & translate"
  // button for the new language while a stale reply for the old one lands
  // harmlessly — it simply no longer matches `requestKey` below.
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [genError, setGenError] = useState<{
    requestKey: string;
    failure: GlossFailure;
  } | null>(null);
  // Only the async stale-session signal (server rejected an authed call) is
  // state; the ordinary signed-out miss is derived during render below.
  const [staleSession, setStaleSession] = useState(false);
  // The allowance refusal, when the last generate attempt was rate-limited.
  // Async (only the server knows), so state — and cleared by the remount that
  // every new selection already causes.
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);
  // The clock keeps "in 12 minutes" honest while the popover sits open.
  const coarseNow = useCoarseNow();
  // Not skipped until a refusal: skipping guarantees the button renders
  // "Read without the wait" and then swaps to a priced label a round trip
  // later — an accessible name changing under a focused control, and exactly
  // what `subscribe-prompt.tsx` says not to do. Both queries are tiny,
  // argument-free, and shared across every popover by Convex's subscription
  // dedupe, so asking early costs a single fetch per session.
  const plans = useQuery(api.subscriptions.availablePlans, {});
  // Held across the minute tick (`useLastLoaded`): `now` is a query argument,
  // so `quota` goes `undefined` for a round trip every time the clock
  // advances, and `promptOffer` below reads a missing quota as the FREE tier.
  // Unheld, that is the same defect the comment above refuses — an all-access
  // subscriber refused at their daily cap should be offered nothing, but once
  // a minute the fallback would offer them the cheapest plan (one they
  // already exceed), materialising a priced button inside an open popover and
  // taking it away again a round trip later.
  const quota = useLastLoaded(
    useQuery(api.ai.translationQuota, { now: coarseNow }),
  );
  // Only to decide whether the founder notice is owed. Skipped while signed
  // out (`myRole` is `null` for every anonymous caller), matching `app-nav`.
  const adminRole = useQuery(api.admins.myRole, isAuthenticated ? {} : "skip");
  // Mirrored into a ref — the `logRef` idiom above — so the generate call's
  // success callback reads the values current when it LANDS, not the ones
  // its click closed over a round trip earlier.
  const founderNoticeRef = useRef({ adminRole, quota, coarseNow });
  useEffect(() => {
    founderNoticeRef.current = { adminRole, quota, coarseNow };
  });
  // What is worth offering THIS reader: the cheapest plan that would actually
  // raise the cap they just hit. A reader refused on the $4 plan is offered
  // all-access; a reader refused on all-access is offered nothing, because
  // nothing we sell would help. Without this, a paying subscriber who hits
  // 50/hour is sold the plan they already hold — and since only paid tiers
  // have a daily cap at all, "Out for today" could ONLY ever be shown to
  // someone already paying.
  const promptOffer = useMemo(() => {
    if (plans === undefined) return null;
    const held = quota?.tier ?? "free";
    const better = plans.filter((plan) =>
      held === "free" ? true : plan.allAccess && held !== "all_access",
    );
    const cheapest = [...better].sort(
      (a, b) => a.amountCents - b.amountCents,
    )[0];
    return cheapest === undefined
      ? null
      : {
          planId: cheapest.planId,
          displayName: cheapest.displayName,
          amountCents: cheapest.amountCents,
          currency: cheapest.currency,
        };
  }, [plans, quota]);

  // A refusal is a status message: the tabs body silently swaps "Translating…"
  // for the slip, which a screen-reader user is never told about (WCAG 4.1.3).
  // The card already owns two alternating live regions for exactly this, built
  // for the engagement gate — reuse them rather than adding a third.
  const [announcedRefusal, setAnnouncedRefusal] = useState<QuotaRefusal | null>(
    null,
  );
  if (refusal !== announcedRefusal) {
    setAnnouncedRefusal(refusal);
    if (refusal !== null) {
      setAnnouncement((previous) =>
        announce(
          previous,
          refusal.scope === "day"
            ? `That is your ${String(refusal.limit)} translations for today. Back ${retryPhrase(refusal.retryAt, coarseNow)}.`
            : `That is your ${String(refusal.limit)} translations for this hour. Back ${retryPhrase(refusal.retryAt, coarseNow)}.`,
        ),
      );
    }
  }

  // A new target language is a NEW request (see `requestKey` below), and the popover does not remount for it — its key is the
  // selection span. Without this the "allowance spent" slip keeps rendering
  // over a language the reader has not been refused for. Same
  // adjust-during-render pattern as `prevAuthenticated` above.
  const [prevTargetLang, setPrevTargetLang] = useState(targetLang);
  if (prevTargetLang !== targetLang) {
    setPrevTargetLang(targetLang);
    setRefusal(null);
  }

  const resolvedMiss = key !== null && cached === null;
  const showSignIn =
    (resolvedMiss && !consensusPending && !isAuthenticated) || staleSession;

  // staleSession only ever flips true from the catch block below; without
  // this it stays true forever even after a sign-in (e.g. completed in
  // another tab), permanently showing the sign-in state for a selection
  // whose generation was never retried. Un-stick it the moment
  // isAuthenticated flips true — adjusted during render (the React-sanctioned
  // "reset state when a prop changes" pattern: a tracked previous value in
  // state, not a ref, so no `react-hooks/refs` render-time access; the
  // setState calls happen in the render body, not inside an effect, so no
  // `react-hooks/set-state-in-effect` either).
  const [prevAuthenticated, setPrevAuthenticated] = useState(isAuthenticated);
  if (prevAuthenticated !== isAuthenticated) {
    setPrevAuthenticated(isAuthenticated);
    if (isAuthenticated) setStaleSession(false);
  }

  // Keyed on (hash, citation, targetLang), not the hash alone — a mid-popover
  // targetLang change is a fresh cache key (the popover's `key` prop only
  // remounts on reselect, not on a language change).
  const requestKey = key === null ? null : `${key}:${citation}:${targetLang}`;
  const generating = requestKey !== null && pendingKey === requestKey;
  // A selection the server's input guard would refuse is explained up front
  // rather than offered as a button that can only fail — same guard, run
  // locally, so no round trip is spent learning the answer.
  const inputFailure = useMemo(
    () => glossInputFailure(norm.quote, paragraph),
    [norm.quote, paragraph],
  );
  const failure =
    inputFailure ??
    (genError !== null && genError.requestKey === requestKey
      ? genError.failure
      : null);
  // The explicit generate affordance stands on a resolved miss for a
  // signed-in reader once nothing else claims the body: not while the
  // consensus lookup is unresolved, not behind the gate (which only stands
  // when something IS cached, and nothing may be spent behind the mask), and
  // not over a sign-in or allowance slip.
  const offerGenerate =
    resolvedMiss &&
    !consensusPending &&
    !gated &&
    isAuthenticated &&
    !staleSession &&
    refusal === null;

  // The ONE Claude call, fired only from the reader's click (PORT-PREP: a
  // paid generation is an explicit reader action, never a consequence of
  // looking). From an event handler rather than an effect, so no dependency
  // churn — a language change, a sign-in landing, a quota tick — can spend
  // on the reader's behalf or orphan a request mid-flight
  // (react-useeffect-discipline §1.5/§1.6).
  const requestGeneration = (): void => {
    if (requestKey === null || generating || inputFailure !== null) return;
    const thisKey = requestKey;
    setPendingKey(thisKey);
    setGenError(null);
    // CLAIM THE HIT COUNTER FOR THIS LOOKUP BEFORE GENERATING (review, sales
    // branch). A successful generation writes into `aiCache`, which pushes the
    // `getCached` subscription above from null to a value — and the hit effect,
    // seeing a resolved cache, would count this same lookup as a HIT as well as
    // the miss the action records. Every miss became one hit and one miss, so
    // the panel's hit rate could never read below 50% no matter how cold the
    // cache was. Marking the key here makes the arriving value what it is: the
    // answer to a miss we already counted.
    countedHitRef.current = thisKey;
    logRef.current.event("gloss.generate", {
      citation,
      targetLang,
      sourceLanguage,
    });
    // `pendingKey` is deliberately NOT cleared on success: the action resolves
    // once the row is written, and the `getCached` push can trail it by a
    // round trip. Clearing it would flash the idle button back for that
    // round trip; leaving it shows "Glossing…" until the result replaces it.
    const settleFailure = (failure: GlossFailure | null): void => {
      setPendingKey((k) => (k === thisKey ? null : k));
      if (failure !== null) setGenError({ requestKey: thisKey, failure });
    };
    generate({
      bookSlug,
      chapterNumber,
      quote: norm.quote,
      start: norm.start,
      end: norm.end,
      targetLang,
      // Groups this miss with the reader's other lookups. Load-bearing for
      // nothing: an absent key means uncounted, never unserved.
      sessionKey: getSessionId(),
    }).then(
      () => {
        // Success path only: a refused or failed call has nothing to tell a
        // founder, and this must never fire alongside the `refusal` prompt.
        // Fires even if the reader has since closed the popover — the call
        // landed and the allowance is spent, and the toast is an app-level
        // surface rather than part of this card.
        const notice = founderNoticeRef.current;
        notifyFounderPastLimit(
          notice.adminRole,
          notice.quota,
          notice.coarseNow,
        );
      },
      (error: unknown) => {
        if (isUnauthenticated(error)) {
          settleFailure(null);
          setStaleSession(true);
          return;
        }
        // Out of allowance — an ordinary state with its own affordance, not
        // an error to show in red.
        const refused = readQuotaError(error);
        if (refused !== null) {
          settleFailure(null);
          setRefusal(refused);
          logRef.current.event("gloss.quota_prompt", {
            citation,
            targetLang,
            scope: refused.scope,
          });
          return;
        }
        // Reader-facing copy, never a machine code or Convex's opaque
        // "[CONVEX A(ai:glossSelection)] Server Error" text — logged in full,
        // shown as `describeGlossFailure` words it.
        settleFailure(describeGlossFailure(error));
        logRef.current.error("gloss.error", error, { citation, targetLang });
      },
    );
  };

  // Analytics only (no state) — record when the sign-in affordance is shown.
  // Not while the gate masks the card: nothing is offered there yet.
  useEffect(() => {
    if (showSignIn && !gated) {
      logRef.current.event("gloss.signin_prompt", { citation, targetLang });
    }
  }, [showSignIn, gated, citation, targetLang]);

  const result: GlossResult | null = cached ?? null;
  const meta = result
    ? [result.lemma, result.pos].filter((v) => v && v !== "").join(" · ")
    : "";

  // Which of the card's three mutually exclusive bodies is rendered, and
  // whether it carries the generate button. Mirrors the JSX conditions below,
  // and exists only as the discriminator for the focus rescue: a body swap
  // replaces every focusable element in the card, and so does the result
  // arriving under a reader who pressed "Gloss & translate" — the button
  // they are focused on unmounts with the miss it answered.
  const bodyKind: "gate" | "consensus" | "tabs" = gated
    ? "gate"
    : result === null && consensusHit && consensus
      ? "consensus"
      : "tabs";
  const body = offerGenerate ? `${bodyKind}+generate` : bodyKind;

  // Keep the reader's focus inside the card across those swaps. A gated word
  // is the case that made this necessary: the card opens on the Tabs (nothing
  // is known to be cached yet, so nothing is gated yet), Radix lands focus on
  // the Gloss trigger, and then the consensus query resolves and replaces the
  // whole Tabs subtree with the gate — dropping focus to <body> exactly where
  // the reader has work to do. Matching a word does the same in reverse.
  //
  // Only ever a rescue: it acts on a SWAP, never on the first render, and only
  // when focus has already left the card. Radix owns the opening focus, and
  // this must not race it — a child's effect runs before its parent's, so
  // reaching in on mount lands focus somewhere Radix's own autofocus then
  // moves again, costing the ungated card its "Enter puts you on the Gloss
  // tab, zero further Tabs" contract. Measured, not assumed.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const shownBodyRef = useRef(body);
  useEffect(() => {
    const swappedFrom = shownBodyRef.current;
    shownBodyRef.current = body;
    const content = contentRef.current;
    if (!ownsFocus || !content || swappedFrom === body) return;
    if (content.contains(document.activeElement)) return;
    // The popover content itself is the fallback landing spot (Radix gives it
    // `tabIndex={-1}`), for the bodies that render nothing tabbable at all —
    // the consensus reveal once its generate button has done its work.
    const target =
      firstTabbable(content) ??
      content.closest<HTMLElement>('[data-slot="popover-content"]');
    target?.focus({ preventScroll: true });
  }, [ownsFocus, body]);

  const onGatePick = (pick: GatePick): void => {
    // The engagement signal, as a wide event (logging policy; no Convex
    // fluency tables — retells has no anonymous write path). The words are
    // public-domain book text, not PII.
    logRef.current.event("gloss.gate_pick", {
      citation,
      sourceLanguage,
      targetLang,
      word: foldQuote(norm.quote),
      chosen: pick.chosen,
      correct: pick.correct,
      cachedKind: exactHit ? "exact" : "consensus",
    });
    setAnnouncement((previous) =>
      announce(
        previous,
        pick.correct
          ? "Correct. Its translation follows."
          : "Not quite — try again with a fresh set of words.",
      ),
    );
    if (pick.correct) setUnlocked(true);
  };

  return (
    <div ref={contentRef} className="flex flex-col gap-3">
      {/* The gate's only non-visual feedback. Rendered unconditionally and
          from the very first paint, so the regions are in the DOM well before
          anything is written to them (a live region introduced and filled in
          the same frame is routinely missed) and so they outlive the gate. */}
      <div className="sr-only">
        <output aria-live="polite">
          {announcement?.slot === 0 ? announcement.text : ""}
        </output>
        <output aria-live="polite">
          {announcement?.slot === 1 ? announcement.text : ""}
        </output>
      </div>

      {/* A plain `div`, NOT a `<header>`: the popover content is portaled to
          `document.body`, so a `<header>` here is not nested in any sectioning
          element and becomes a second BANNER landmark for the whole document —
          axe `landmark-no-duplicate-banner` + `landmark-unique`, both against
          the reader's real page header, for as long as a gloss is open. */}
      <div className="border-border/60 flex items-baseline justify-between gap-3 border-b pb-2">
        {gated ? (
          // The headword is part of the answer — masked until matched (the
          // word itself still stands selected in the page, which is exactly
          // where the gate wants the reader's eyes).
          <p className="text-muted-foreground min-w-0 font-serif text-base leading-snug tracking-[0.3em] select-none">
            {/* `aria-label` is PROHIBITED on a paragraph (axe
                `aria-prohibited-attr`) — a role that names itself from its
                content is named by giving it content, so the label rides in
                an `sr-only` span instead of an attribute screen readers are
                entitled to ignore. */}
            <span className="sr-only">Word hidden until matched</span>
            <span aria-hidden="true">···</span>
          </p>
        ) : (
          <p
            className="text-foreground min-w-0 font-serif text-base leading-snug"
            lang={sourceLanguage}
          >
            {norm.quote}
          </p>
        )}
        <span className="text-muted-foreground shrink-0 font-serif text-[10px] tracking-widest uppercase lining-nums tabular-nums">
          {citation}
        </span>
      </div>

      {/* The action slot. Its own row rather than a third item in the header:
          the card is 20rem wide and the header's quote already wraps. Set in
          the card's printed-apparatus grammar — an Alegreya SC small-caps
          rubric with a dotted leader, the same row the gate's "Match the word" uses —
          so an admin surface reads as part of the same page rather than as
          chrome bolted onto it. */}
      {!gated && action !== undefined && (
        <div className="-mt-1 flex items-center gap-2">
          <span className="font-smcp text-muted-foreground shrink-0 text-[13px] tracking-[0.219em] lowercase">
            Anchor
          </span>
          <span
            aria-hidden="true"
            className="border-rule h-0 flex-1 border-b border-dotted"
          />
          {action(norm)}
        </div>
      )}

      {!gated && meta !== "" && (
        <p className="text-muted-foreground -mt-1 font-serif text-xs italic">
          {meta}
        </p>
      )}

      {gated ? (
        <GateShell
          quote={norm.quote}
          forms={forms}
          lang={sourceLanguage}
          onPick={onGatePick}
        />
      ) : result === null && consensusHit && consensus ? (
        // Consensus-only reveal: this word has never been glossed on its own,
        // but derived rows elsewhere in the corpus agree on a translation —
        // served free, no tabs (there is no gloss/literal to facet).
        <div className="flex flex-col gap-2 pt-0.5">
          <div className="flex items-baseline gap-2">
            <p
              className="text-foreground min-w-0 font-serif text-sm leading-relaxed"
              lang={targetLang}
            >
              {consensus.translation}
            </p>
            {/* `count` is the rows that agreed with the WINNER; `total`
                counts every voting row, losers included. The badge claims
                agreement, so it must show `count` — "path ×6" over five rows
                saying "path" and one saying "way" is simply false. */}
            {consensus.count > 1 && (
              <span
                className="text-muted-foreground shrink-0 font-serif text-[10px] tracking-wide lining-nums tabular-nums"
                title={`agreed across ${String(consensus.count)} cached passages`}
              >
                ×{String(consensus.count)}
              </span>
            )}
          </div>
          <p className="text-muted-foreground font-serif text-xs italic">
            As translated elsewhere in this book.
          </p>
          {showSignIn ? (
            <div className="border-border/60 flex flex-col items-start gap-2 border-t pt-2">
              <p className="text-muted-foreground font-serif text-sm">
                Sign in for the full gloss.
              </p>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  logRef.current.event("gloss.signin_click", { citation });
                  signIn();
                }}
              >
                Sign in
              </Button>
            </div>
          ) : refusal !== null ? (
            <SubscribePrompt
              refusal={refusal}
              now={coarseNow}
              offer={promptOffer}
              next={`/read/${bookSlug}/${String(chapterNumber)}`}
              onSubscribeClick={() => {
                logRef.current.event("gloss.quota_subscribe_click", {
                  citation,
                  scope: refusal.scope,
                });
              }}
            />
          ) : offerGenerate ? (
            // The contextual upgrade over the consensus line is a paid call
            // like any other miss — offered, never fired on the gate clear.
            <div className="border-border/60 border-t pt-2">
              <GenerateControl
                prompt="Gloss it in this passage’s own context."
                label="Gloss in context"
                generating={generating}
                failure={failure}
                onGenerate={requestGeneration}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <Tabs value={facet} onValueChange={(v) => setFacet(v as Facet)}>
          <TabsList variant="line" className="w-full justify-start">
            {FACETS.map(({ id, label, Icon }) => (
              <TabsTrigger key={id} value={id} className="gap-1.5 px-2 text-xs">
                <Icon aria-hidden className="size-3.5" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="min-h-[3.5rem] pt-1">
            {key === null || cached === undefined || consensusPending ? (
              <p className="text-muted-foreground font-serif text-sm">
                Looking this up…
              </p>
            ) : result ? (
              FACETS.map(({ id }) => (
                <TabsContent key={id} value={id} className="mt-0">
                  <FacetText
                    value={result[id]}
                    lang={id === "literal" ? sourceLanguage : undefined}
                  />
                </TabsContent>
              ))
            ) : showSignIn ? (
              <div className="flex flex-col items-start gap-2">
                <p className="text-muted-foreground font-serif text-sm">
                  Sign in to translate this passage.
                </p>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    logRef.current.event("gloss.signin_click", { citation });
                    signIn();
                  }}
                >
                  Sign in
                </Button>
              </div>
            ) : refusal !== null ? (
              <SubscribePrompt
                refusal={refusal}
                now={coarseNow}
                offer={promptOffer}
                next={`/read/${bookSlug}/${String(chapterNumber)}`}
                onSubscribeClick={() => {
                  logRef.current.event("gloss.quota_subscribe_click", {
                    citation,
                    scope: refusal.scope,
                  });
                }}
              />
            ) : offerGenerate ? (
              // One call fills all three facets, so the button is the same
              // whichever tab is showing.
              <GenerateControl
                prompt="Not yet glossed."
                label="Gloss & translate"
                generating={generating}
                failure={failure}
                onGenerate={requestGeneration}
              />
            ) : null}
          </div>
        </Tabs>
      )}
    </div>
  );
}
